import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  LoginRequestSchema,
  roleLabelFor,
  type LoginRequest,
  type SessionUser,
} from '@datazo/contracts';
import { PenSquare, FileText, History as HistoryIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuthStore } from '@/shared/store/auth';
import { apiFetch, ApiClientError } from '@/shared/api/client';

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: {
      correo: 'jeiner.ramos09@gmail.com',
      password: '',
      remember: true,
    },
  });

  // Registramos `remember` para que RHF lo trackee aunque el Checkbox
  // de Radix se controle vía setValue.
  register('remember');
  const remember = watch('remember');

  const onSubmit = async (values: LoginRequest): Promise<void> => {
    setSubmitError(null);
    setSubmitting(true);
    const from = (location.state as LocationState | null)?.from ?? '/dashboard';

    // Intento real al backend — si falla (no levantado en local), caemos al mock.
    try {
      const response = await apiFetch<{ user: SessionUser }>('/api/auth/login', {
        method: 'POST',
        body: values,
        _skipAuth: true,
      });
      // `apiFetch` ya capturó el access token del header `x-access-token`
      // y lo guardó en el store. Lo leemos para pasarlo a `login()`.
      const accessToken = useAuthStore.getState().accessToken;
      if (accessToken === null || accessToken === '') {
        setSubmitError(
          'No fue posible completar el inicio de sesión (respuesta incompleta del servidor). Inténtalo de nuevo.',
        );
        setSubmitting(false);
        return;
      }
      login(
        {
          id: response.user.id,
          nombre: response.user.nombre,
          correo: response.user.correo,
          rol: response.user.rol,
          rolLabel: roleLabelFor(response.user),
          permisos: response.user.permisos,
          especialidad: response.user.especialidad,
          registroProfesional: response.user.registroProfesional,
          firmaUrl: response.user.firmaUrl,
          mustChangePassword: response.user.mustChangePassword,
          esSuperAdmin: response.user.esSuperAdmin,
        },
        accessToken,
      );
      navigate(from, { replace: true });
      return;
    } catch (err) {
      // 4xx → credenciales/políticas. 5xx o red → conexión.
      if (err instanceof ApiClientError && err.status >= 400 && err.status < 500) {
        // 429 = rate-limiter (5 intentos / 15 min). Merece su propio mensaje:
        // decir "credenciales incorrectas" ahí confunde, porque el backend ya
        // ni siquiera está validando la contraseña.
        // Para el resto mantenemos un mensaje GENÉRICO a propósito: no revelar
        // si el correo existe, está inactivo o sin acceso (evita enumeración
        // de cuentas), aunque el backend distinga esos casos internamente.
        setSubmitError(
          err.status === 429
            ? 'Demasiados intentos fallidos. Espera unos minutos e inténtalo de nuevo.'
            : 'Correo o contraseña incorrectos. Verifica los datos e inténtalo de nuevo.',
        );
        setSubmitting(false);
        return;
      }
      // SECURITY: el login mock SOLO existe en modo desarrollo. En build de
      // producción (Vite `import.meta.env.DEV === false`) este bloque
      // se elimina por dead-code elimination y el usuario verá un error real.
      if (import.meta.env.DEV) {
        // Token sintético — el backend está caído así que las queries posteriores
        // fallarán igual, pero al menos entramos al shell para ver el layout.
        login(
          {
            id: 'U-001',
            nombre: 'Jeiner Ramos (DEV mock)',
            correo: values.correo,
            rol: 'admin',
            rolLabel: roleLabelFor({ rol: 'admin', esSuperAdmin: true }),
            permisos: ['*'],
            esSuperAdmin: true,
          },
          'dev-mock-token',
        );
        navigate(from, { replace: true });
        setSubmitting(false);
        return;
      }
      setSubmitError(
        'No fue posible conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.',
      );
      setSubmitting(false);
      return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-bg">
      {/* Hero */}
      <div className="hidden lg:flex flex-col justify-between p-16 text-slate-700 relative overflow-hidden bg-gradient-to-br from-[#f1f5f9] via-[#e9edf3] to-[#e2e8f0]">
        <div
          aria-hidden
          className="absolute -right-[200px] -bottom-[200px] w-[500px] h-[500px] rounded-full bg-white/50"
        />
        <div
          aria-hidden
          className="absolute -left-[100px] -top-[100px] w-[300px] h-[300px] rounded-full bg-slate-300/25"
        />

        <div className="relative z-10 flex flex-col items-start gap-2">
          <img
            src="/logo-endomedical.png"
            alt="Endomedical"
            className="h-40 md:h-56 w-auto max-w-full object-contain drop-shadow-md"
          />
          <span className="text-slate-500 text-xs tracking-widest">
            IPS · Plataforma clínica
          </span>
        </div>

        <div className="relative z-10 max-w-[440px]">
          <h2 className="text-4xl font-extrabold leading-[1.1] tracking-tight mb-5 text-slate-900">
            Control clínico digital
            <br />
            para cada procedimiento.
          </h2>
          <p className="text-[15px] leading-relaxed text-slate-600">
            Consentimiento informado, registro de anestesia y hoja de gasto — firmados en tablet,
            asociados al paciente, con trazabilidad completa y disponibles cuando los necesites.
          </p>

          <div className="grid gap-4 mt-7">
            {[
              {
                icon: <PenSquare className="w-5 h-5" />,
                title: 'Firma digital en tablet',
                desc: 'Captura legal del paciente, médico, anestesiólogo y enfermera.',
              },
              {
                icon: <FileText className="w-5 h-5" />,
                title: 'PDF inalterable por documento',
                desc: 'Generado automáticamente y vinculado al episodio clínico.',
              },
              {
                icon: <HistoryIcon className="w-5 h-5" />,
                title: 'Historial por paciente',
                desc: 'Un expediente digital por cada visita; nunca duplicado.',
              },
            ].map((feat) => (
              <div key={feat.title} className="flex gap-3.5 items-start">
                <div className="w-10 h-10 rounded-md bg-white text-primary shadow-sm grid place-items-center flex-shrink-0">
                  {feat.icon}
                </div>
                <div>
                  <strong className="text-sm font-semibold block text-slate-800">
                    {feat.title}
                  </strong>
                  <span className="text-[13px] text-slate-500 block mt-0.5">{feat.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500">
          © 2026 Endomedical IPS · Todos los derechos reservados
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-10">
        <div className="w-full max-w-[400px]">
          <h1 className="text-2xl font-extrabold mb-1 tracking-tight">Bienvenido</h1>
          <p className="text-text-muted text-[13.5px] mb-7">
            Ingresa con tu cuenta institucional para continuar.
          </p>

          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
            <div className="flex flex-col gap-1.5 mb-3">
              <Label htmlFor="correo">Usuario o correo</Label>
              <Input
                id="correo"
                type="email"
                autoComplete="username"
                aria-invalid={errors.correo !== undefined}
                {...register('correo')}
              />
              {errors.correo?.message !== undefined && (
                <p className="text-[11.5px] text-danger mt-1">{errors.correo.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5 mb-3">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password !== undefined}
                {...register('password')}
              />
              {errors.password?.message !== undefined && (
                <p className="text-[11.5px] text-danger mt-1">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <label className="inline-flex items-center gap-2 text-sm text-text cursor-pointer select-none">
                <Checkbox
                  checked={remember}
                  onCheckedChange={(v) => setValue('remember', v === true)}
                />
                Recordar sesión
              </label>
              <a href="#" className="text-sm text-primary font-semibold hover:underline">
                ¿Olvidó la contraseña?
              </a>
            </div>

            {submitError !== null && (
              <div className="mb-3 px-3 py-2 rounded-md bg-danger-soft text-red-800 text-[12.5px] border border-red-200">
                {submitError}
              </div>
            )}

            <Button type="submit" size="lg" disabled={submitting} className="w-full">
              {submitting ? 'Ingresando…' : 'Ingresar a la plataforma'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
